import React, { Component } from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { Link } from 'react-router-dom';
interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}
interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }
  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorInfo: null
    };
  }
  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error to error reporting service (e.g., Sentry)
    console.error('Error caught by boundary:', error, errorInfo);
    this.setState({
      error,
      errorInfo
    });
    // TODO: Send to error tracking service
    // Sentry.captureException(error, { extra: errorInfo })
  }
  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
  };
  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }
      return <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4" data-id="element-2355">
          <div className="max-w-md w-full" data-id="element-2356">
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 p-8 text-center" data-id="element-2357">
              {/* Error Icon */}
              <div className="mx-auto w-16 h-16 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center mb-6" data-id="element-2358">
                <AlertTriangle className="h-8 w-8 text-red-600 dark:text-red-400" data-id="element-2359" />
              </div>

              {/* Error Message */}
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2" data-id="element-2360">
                Something went wrong
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mb-6" data-id="element-2361">
                We're sorry, but something unexpected happened. Our team has
                been notified.
              </p>

              {/* Error Details (Development only) */}
              {process.env.NODE_ENV === 'development' && this.state.error && <div className="mb-6 p-4 bg-gray-100 dark:bg-gray-900 rounded-lg text-left overflow-auto max-h-48" data-id="element-2362">
                  <p className="text-xs font-mono text-red-600 dark:text-red-400 mb-2" data-id="element-2363">
                    {this.state.error.toString()}
                  </p>
                  {this.state.errorInfo && <pre className="text-xs text-gray-600 dark:text-gray-400 whitespace-pre-wrap" data-id="element-2364">
                      {this.state.errorInfo.componentStack}
                    </pre>}
                </div>}

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3" data-id="element-2365">
                <button onClick={this.handleReset} className="flex-1 inline-flex items-center justify-center px-4 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors" data-id="element-2366">
                  <RefreshCw className="h-4 w-4 mr-2" data-id="element-2367" />
                  Try Again
                </button>
                <Link to="/" className="flex-1 inline-flex items-center justify-center px-4 py-3 bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg font-medium hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors" data-id="element-2368">
                  <Home className="h-4 w-4 mr-2" data-id="element-2369" />
                  Go Home
                </Link>
              </div>

              {/* Help Text */}
              <p className="mt-6 text-sm text-gray-500 dark:text-gray-400" data-id="element-2370">
                If this problem persists, please{' '}
                <Link to="/help" className="text-blue-600 dark:text-blue-400 hover:underline" data-id="element-2371">
                  contact support
                </Link>
              </p>
            </div>
          </div>
        </div>;
    }
    return this.props.children;
  }
}
// Functional wrapper for easier use
export function withErrorBoundary<P extends object>(Component: ComponentType<P>, fallback?: ReactNode) {
  return function WithErrorBoundaryWrapper(props: P) {
    return <ErrorBoundary fallback={fallback} data-id="element-2372">
        <Component {...props} data-id="element-2373" />
      </ErrorBoundary>;
  };
}